import { Button, Dropdown, Layout, Segmented, Typography } from 'antd';
import { Content, Footer, Header } from 'antd/es/layout/layout';
import { Outlet, useNavigate, ScrollRestoration } from 'react-router-dom';
import { useGAPageTracking } from '../../hooks/useGAPageTracking';
import { Suspense } from 'react';
import { Loading } from '../components/common/Loading';
import ErrorBoundary from '../components/common/ErrorBoundary';
import { useAuth } from '../../hooks/useAuth';
import { MenuOutlined } from '@ant-design/icons';
import useMobile from '../../hooks/useMobile';
import { useTranslation } from 'react-i18next';
import { setLang } from '../../i18n';
import { usePlanGuard } from '../../hooks/usePlanGuard';

export const AccountLayout = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const { t, i18n } = useTranslation();
  const { guard } = usePlanGuard();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'ja';
  useGAPageTracking();

  // 言語切替。ライトプラン以外は共通のアップグレード誘導ダイアログを出して切り替えない。
  const handleLangChange = (val: string) => {
    guard('multilingual', () => setLang(val as 'ja' | 'en'));
  };

  // ヘッダーに出すログイン中のユーザ名。名前が未設定ならメールアドレスで代替し、
  // どちらも取れていない（読み込み中など）ときは何も表示しない。
  const displayName = auth.account?.name || auth.account?.email;

  const menuItems = [
    {
      label: t('menu.top'),
      key: 'menu1',
      onClick: () => (window.location.href = `https://ohat.jp`),
    },
    {
      label: t('menu.admin'),
      key: 'menu2',
      onClick: () => (window.location.href = `https://app.ohat.jp`),
    },
    {
      label: t('menu.howto'),
      key: 'menu3',
      onClick: () => (window.location.href = `https://ohat.jp/howto`),
    },
    {
      label: t('menu.plans'),
      key: 'menu4',
      onClick: () => (window.location.href = `https://www.ohat.jp/plans`),
    },
    {
      label: t('menu.blog'),
      key: 'menu5',
      onClick: () => (window.location.href = `https://www.ohat.jp/blog`),
    },
    // ケアプロトコル・プラン管理・メンバー管理は事業者情報カードのボタンに集約したためメニューからは除外
    {
      label: t('menu.logout'),
      key: 'menu6',
      onClick: () => auth.signOutCognito(),
    },
  ];

  return (
    <Layout
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      <ScrollRestoration />
      <Header
        style={{
          position: 'sticky',
          top: 0,
          // antd の Radio.Button など z-index: 1 を持つ要素がヘッダーを貫通して
          // 見えてしまうため、コンテンツより十分高い値にする（AdminLayout と揃える）。
          zIndex: 100,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'white',
          padding: 0,
          boxShadow: '0 1px 5px 0 rgba(0, 0, 0, .1)',
        }}
      >
        <div style={{ flex: 1 }}></div>
        <img
          src={`${process.env.PUBLIC_URL}/logo.svg`}
          alt="Logo"
          height={50}
          onClick={() => navigate('/')}
          style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            cursor: 'pointer',
          }}
        />
        <div style={{ flex: 1 }}></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* モバイルはヘッダーの横幅が厳しいため、メニューのラベルと同様に非表示にする。 */}
          {!isMobile() && displayName && (
            <Typography.Text
              type="secondary"
              ellipsis
              style={{ maxWidth: 160 }}
            >
              {displayName}
            </Typography.Text>
          )}
          {/* モバイルはヘッダーの横幅が足りず Segmented が折り返して崩れるため、
              国旗だけのトグルボタンにする。出すのは切替先ではなく「今の言語」。
              PC の Segmented が選択中を強調するのと見え方を揃え、
              国旗が現在の状態を表すようにする（切替できることは title で補う）。 */}
          {isMobile() ? (
            <Button
              type="text"
              aria-label={t('common.switchLanguage')}
              title={t('common.switchLanguage')}
              onClick={() => handleLangChange(lang === 'ja' ? 'en' : 'ja')}
              style={{ fontSize: 20, lineHeight: 1, padding: '0 4px' }}
            >
              {lang === 'ja' ? '🇯🇵' : '🇬🇧'}
            </Button>
          ) : (
            <Segmented
              size="small"
              value={lang}
              onChange={handleLangChange}
              options={[
                { label: '🇯🇵 日本語', value: 'ja' },
                { label: '🇬🇧 English', value: 'en' },
              ]}
            />
          )}
          {/* 横型Menu(rc-overflow)は言語切替でラベルが消える不具合があるため、
              オーバーフロー計測のない Dropdown で置き換える。 */}
          <Dropdown menu={{ items: menuItems }} trigger={['hover', 'click']}>
            <Button type="text" icon={<MenuOutlined />}>
              {isMobile() ? null : t('menu.menu')}
            </Button>
          </Dropdown>
        </div>
      </Header>
      <Layout>
        <Content
          style={{
            flexGrow: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start', // 追加: 縦方向の配置を上端に揃える
            padding: 16,
            background: '#F5FAFF',
          }}
        >
          <ErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </Content>
      </Layout>
      <Footer style={{ textAlign: 'center', background: 'rgb(237 237 237)' }}>
        {t('footer.copyright')}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
          <a
            href="https://www.ohat.jp/%E5%88%A9%E7%94%A8%E8%A6%8F%E7%B4%84"
            target="_blank"
            rel="noreferrer"
          >
            <Typography.Text
              style={{
                paddingTop: 4,
                fontSize: 14,
                textAlign: 'center',
                textDecorationLine: 'underline',
              }}
            >
              {t('footer.terms')}
            </Typography.Text>
          </a>
          <a
            href="https://medi-banx.com/privacy/"
            target="_blank"
            rel="noreferrer"
          >
            <Typography.Text
              style={{
                paddingTop: 4,
                fontSize: 14,
                textAlign: 'center',
                textDecorationLine: 'underline',
              }}
            >
              {t('footer.privacy')}
            </Typography.Text>
          </a>
          <a
            href="https://medi-banx.com/contact/"
            target="_blank"
            rel="noreferrer"
          >
            <Typography.Text
              style={{
                paddingTop: 4,
                fontSize: 14,
                textAlign: 'center',
                textDecorationLine: 'underline',
              }}
            >
              {t('footer.contact')}
            </Typography.Text>
          </a>
        </div>
      </Footer>
    </Layout>
  );
};
